import React from "react";

export default function Terms() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <h1 className="text-4xl font-bold mb-4">Terms and Conditions</h1>

      <p className="text-gray-600 mb-8">
        Effective Date: January 1, 2025 | Last Updated: January 1, 2025
      </p>

      <section className="space-y-6">
        <p>
          Welcome to SoftConcern Technologies LLP ("SoftConcern", "Company",
          "we", "our", or "us"). These Terms and Conditions ("Terms") govern
          your access to and use of our website (https://softconcern.com),
          mobile applications, WhatsApp Business communications, and all
          related services (collectively, the "Services"). By accessing or
          using our Services, you agree to be bound by these Terms.
        </p>

        <div>
          <h2 className="text-2xl font-semibold mb-3">1. Acceptance of Terms</h2>
          <p>
            By accessing or using our Services, you confirm that you are at
            least 18 years of age and have the legal capacity to enter into
            these Terms. If you are using our Services on behalf of a business
            or organization, you represent that you have authority to bind that
            entity to these Terms.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">2. Services Overview</h2>
          <p>
            SoftConcern Technologies LLP provides IT consulting, software
            development, mobile application development, web development,
            AI/ML solutions, cloud services, UI/UX design, and related
            technology services. We also communicate with clients and prospects
            through WhatsApp Business for project updates, support, and service
            notifications.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">3. User Accounts</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>You are responsible for maintaining the confidentiality of any account credentials.</li>
            <li>You agree to provide accurate, current, and complete information.</li>
            <li>You are responsible for all activities that occur under your account.</li>
            <li>You must notify us immediately of any unauthorized use of your account.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">4. Use of Services</h2>
          <p className="mb-2">You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Use the Services for any unlawful purpose or in violation of any applicable laws.</li>
            <li>Attempt to gain unauthorized access to any part of the Services or related systems.</li>
            <li>Interfere with or disrupt the integrity or performance of the Services.</li>
            <li>Transmit any viruses, malware, or harmful code.</li>
            <li>Reverse engineer, decompile, or disassemble any software provided by us.</li>
            <li>Use automated systems (bots, scrapers) to access the Services without permission.</li>
            <li>Impersonate any person or entity or misrepresent your affiliation.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">5. WhatsApp Business Communication</h2>
          <p className="mb-2">
            By initiating or consenting to communication with us via WhatsApp Business, you agree to the following:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>You consent to receive messages related to service inquiries, project updates, appointment confirmations, support responses, and transactional notifications.</li>
            <li>You may opt out of non-essential communications at any time by messaging "STOP".</li>
            <li>We will not share your WhatsApp contact information with third parties for marketing purposes.</li>
            <li>Message frequency varies based on your interaction and service requirements.</li>
            <li>Standard messaging rates from your carrier may apply.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">6. Mobile Applications</h2>
          <p className="mb-2">
            If you download or use any mobile application developed or published by SoftConcern:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>You are granted a limited, non-exclusive, non-transferable, revocable license to use the application for personal or business purposes.</li>
            <li>You may not copy, modify, distribute, sell, or lease any part of the application.</li>
            <li>We may release updates or patches; continued use constitutes acceptance of updates.</li>
            <li>We reserve the right to suspend or terminate access to the application at our discretion.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">7. Intellectual Property</h2>
          <p>
            All content, designs, logos, trademarks, software, code, and
            materials on our website and applications are the property of
            SoftConcern Technologies LLP or its licensors and are protected by
            applicable intellectual property laws. You may not reproduce,
            distribute, modify, or create derivative works without our prior
            written consent.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">8. Payment Terms</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Payment terms for services are governed by individual project agreements or proposals.</li>
            <li>All fees are non-refundable unless otherwise stated in a written agreement.</li>
            <li>We reserve the right to suspend services for overdue payments.</li>
            <li>Applicable taxes (including GST) will be charged as per Indian tax regulations.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">9. Confidentiality</h2>
          <p>
            Both parties agree to maintain the confidentiality of proprietary
            information shared during the course of service delivery. This
            includes but is not limited to business strategies, technical
            specifications, source code, and client data.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">10. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by applicable law, SoftConcern
            Technologies LLP shall not be liable for any indirect, incidental,
            special, consequential, or punitive damages, including but not
            limited to loss of profits, data, business opportunities, or
            goodwill, arising from your use of or inability to use the Services.
            Our total liability shall not exceed the amount paid by you for the
            specific service giving rise to the claim.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">11. Disclaimer of Warranties</h2>
          <p>
            The Services are provided "as is" and "as available" without
            warranties of any kind, either express or implied, including but
            not limited to implied warranties of merchantability, fitness for a
            particular purpose, and non-infringement. We do not warrant that
            the Services will be uninterrupted, error-free, or completely secure.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">12. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless SoftConcern
            Technologies LLP, its directors, employees, and agents from any
            claims, damages, losses, liabilities, and expenses (including legal
            fees) arising from your use of the Services, violation of these
            Terms, or infringement of any third-party rights.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">13. Termination</h2>
          <p>
            We may terminate or suspend your access to the Services at any time,
            with or without cause, and with or without notice. Upon termination,
            your right to use the Services ceases immediately. Provisions that
            by their nature should survive termination shall remain in effect.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">14. Third-Party Links and Services</h2>
          <p>
            Our Services may contain links to third-party websites or services
            that are not owned or controlled by SoftConcern. We have no control
            over, and assume no responsibility for, the content, privacy
            policies, or practices of any third-party sites or services.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">15. Governing Law and Jurisdiction</h2>
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of India. Any disputes arising under these Terms shall be
            subject to the exclusive jurisdiction of the courts in Hyderabad,
            Telangana, India.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">16. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. Changes
            will be effective immediately upon posting on this page. Your
            continued use of the Services after any modifications constitutes
            acceptance of the updated Terms. We encourage you to review this
            page periodically.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">17. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable or
            invalid, that provision shall be limited or eliminated to the
            minimum extent necessary so that the remaining provisions remain in
            full force and effect.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3">18. Contact Us</h2>
          <p>
            If you have any questions about these Terms and Conditions, please
            contact us:
          </p>
          <div className="mt-3 bg-gray-100 p-4 rounded-lg">
            <p><strong>SoftConcern Technologies LLP</strong></p>
            <p>Email: info@softconcern.com</p>
            <p>Phone: +91 9876543210</p>
            <p>Website: https://softconcern.com</p>
          </div>
        </div>
      </section>
    </div>
  );
}
